import FormControlLabel from "@mui/material/FormControlLabel";
import { useQueryClient } from "@tanstack/react-query";
import { RotateCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { IconButton } from "src/components/Default/LekUI/Button";
import { Checkbox } from "src/components/Default/LekUI/Checkbox";
import { Tooltip } from "src/components/Default/LekUI/Tooltip";

import Filter from "./DefaultFilters";

interface AiDiscussionsValue {
	isAiUnapproved: boolean;
	hasUnresolvedReviews: boolean;
}

type AiDiscussionsKey = keyof AiDiscussionsValue;

interface AiDiscussionsFilterProps {
	selectedData: { aiDiscussions: AiDiscussionsValue };
	setSelectedData: (data: { aiDiscussions: AiDiscussionsValue }) => void;
	selected: boolean;
	expanded: boolean;
	onChange: () => void;
}

export function AiDiscussionsFilter(props: AiDiscussionsFilterProps) {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { aiDiscussions } = props.selectedData;

	const handleRefresh = () => {
		queryClient.invalidateQueries({
			queryKey: ["questions"],
		});
	};

	const toggle = (key: AiDiscussionsKey) => {
		props.setSelectedData({
			aiDiscussions: {
				...aiDiscussions,
				[key]: !aiDiscussions[key],
			},
		});
	};

	const renderCheckbox = (key: AiDiscussionsKey, label: string) => (
		<FormControlLabel
			label={label}
			control={
				<Checkbox
					className="checkbox"
					checked={aiDiscussions[key]}
					onChange={() => toggle(key)}
					data-test-id={`ai-discussions-${key}`}
				/>
			}
		/>
	);

	const renderFilters = () => (
		<div className="flex flex-col">
			{renderCheckbox(
				"isAiUnapproved",
				t(
					"filters.exams.aiDiscussions.isAiUnapproved",
					"Pytania bez akceptacji omówienia",
				),
			)}
			{renderCheckbox(
				"hasUnresolvedReviews",
				t(
					"filters.exams.aiDiscussions.hasUnresolvedReviews",
					"Pytania z nierozwiązanymi uwagami",
				),
			)}
		</div>
	);

	const refreshButton = (
		<Tooltip
			title={t("filters.exams.aiDiscussions.refresh", "Odśwież listę pytań")}
		>
			<IconButton
				size="small"
				className="menu-icon-button"
				onClick={(e: React.MouseEvent) => {
					e.stopPropagation();
					handleRefresh();
				}}
				data-test-id="ai-discussions-refresh"
			>
				<RotateCw className="icon-super-small icon-primary" />
			</IconButton>
		</Tooltip>
	);

	return (
		<Filter
			{...props}
			title={t("filters.exams.aiDiscussions.title", "Omówienia AI")}
			renderFilterDetails={renderFilters}
			actions={refreshButton}
		/>
	);
}
